import { faMapLocation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import React from 'react';
import { useParams } from 'react-router-dom';
import { SportsEvent, WithContext } from 'schema-dts';
import Tabs from '~/src/components/Game/Tabs';
import GameCard from '~/src/components/GameCard';
import Layout from '~/src/components/Layout';
import LoadingBoundary from '~/src/components/LoadingBoundary';
import PageHeader from '~/src/components/PageHeader';
import { Urls } from '~/src/lib/enums/urls';
import { OteGame, isComplete } from '~/src/models/OteGame';
import { OteGameOtePlayerOteTeam } from '~/src/models/OteGameOtePlayerOteTeam';
import NotFound from '~/src/overtime-lib/src/www/components/NotFound';
import ValidateUUID from '~/src/overtime-lib/src/www/components/ValidateUUID';
import { useSetDocumentState } from '~/src/overtime-lib/src/www/contexts/Document';
import { useFetchAndSubscribe } from '~/src/overtime-lib/src/www/hooks/useFetch';
import { getEstDateTime } from '~/src/overtime-lib/src/www/lib';
import './Game.scss';

const _Game = () => {
	const { id } = useParams();

	const game = useFetchAndSubscribe<OteGame>({
		url: `${Urls.ApiRestOteGameBaseUrl}/${id}`,
		key: 'ote_game',
	});

	const homeGameTeam = game?.ote_games_ote_teams.find((t) => t.is_home_team);
	const awayGameTeam = game?.ote_games_ote_teams.find((t) => !t.is_home_team);

	const homeTeam = homeGameTeam?.ote_team;
	const awayTeam = awayGameTeam?.ote_team;

	// We can't conditionally apply the hook so instead pass empty data during fetch
	useSetDocumentState(
		game
			? {
					title: `${awayTeam?.name ?? 'TBD'} @ ${homeTeam?.name ?? 'TBD'} - OTE Play-By-Play`,
					description:
						game.status === 'confirmed' || game.status === 'finished'
							? `The ${awayTeam.name} played the ${homeTeam.name} on ${getEstDateTime(game.starts_at).toLocaleString({
									day: 'numeric',
									month: 'long',
									year: 'numeric',
								})} in ${game.city}, ${game.state}. The ${
									homeGameTeam.points > awayGameTeam.points ? homeTeam.name : awayTeam.name
								} won ${Math.max(homeGameTeam.points, awayGameTeam.points)} to ${Math.min(
									homeGameTeam.points,
									awayGameTeam.points,
								)}.`
							: undefined,
				}
			: {},
	);

	const data: WithContext<SportsEvent> = {
		'@context': 'https://schema.org',
		'@type': 'SportsEvent',
		name: game?.competition_name,
		homeTeam: homeTeam
			? {
					'@type': 'SportsTeam',
					name: homeTeam.name,
					url: `https://overtimeelite.com/teams/${homeTeam.slug}`,
				}
			: undefined,
		awayTeam: awayTeam
			? {
					'@type': 'SportsTeam',
					name: awayTeam.name,
					url: `https://overtimeelite.com/teams/${awayTeam.slug}`,
				}
			: undefined,
		startDate: game?.starts_at,
		endDate: DateTime.fromISO(game?.starts_at).plus({ hour: 1.5 }).toISO(),
		location: {
			'@type': 'Place',
			name: game?.location_name ?? (game?.address?.startsWith('230 17th') ? 'OTE Arena' : undefined),
			address: {
				'@type': 'PostalAddress',
				streetAddress: game?.address,
				addressLocality: game?.city,
				addressRegion: game?.state,
				postalCode: game?.zip,
			},
			latitude: game?.latitude,
			longitude: game?.longitude,
		},
		offers: game?.ote_event?.vivenu_id
			? { '@type': 'Offer', url: `https://tickets.overtimeelite.com/event/${game?.ote_event?.vivenu_id}` }
			: undefined,
		eventAttendanceMode: game?.expected_broadcast ? 'MixedEventAttendanceMode' : 'OfflineEventAttendanceMode',
		organizer: { '@type': 'SportsOrganization', name: 'Overtime Elite', url: 'https://overtimeelite.com' },
		sport: 'Basketball',
	};

	const ote_games_ote_players_ote_teams = useFetchAndSubscribe<OteGameOtePlayerOteTeam[]>({
		url: `${Urls.ApiRestOteGameBaseUrl}/${id}/ote_games_ote_players_ote_teams`,
		key: 'ote_games_ote_players_ote_teams',
	});

	if (!game) {
		return <NotFound />;
	}

	return (
		<>
			<div className="GameHeader">
				<GameCard ote_game={game} linkToGamePage={false} />
				{!isComplete(game) ? (
					<div className="ViewingDetailsContainer">
						<div className={`LocationInfo CenterContent`}>
							{game.address ? (
								<a
									className="Location"
									href={`https://www.google.com/maps/place/${game.address},${game.city},${game.state}+${game.zip}`}
								>
									<FontAwesomeIcon icon={faMapLocation} />
									{game.address?.replace(', USA', '')}
								</a>
							) : null}
							{/* {game.ote_event?.cta_url || game.ote_event?.vivenu_id ? (
								<>
									<div className="Tickets desktop-only">
										<FontAwesomeIcon icon={faTicket} />
										<a
											href={
												game.ote_event?.cta_url
													? game.ote_event?.cta_url
													: game.ote_event?.vivenu_id
														? `${Urls.VivenuTicketsEventBaseUrl}/${game.ote_event.vivenu_id}`
														: null
											}
										>
											Buy Tickets
										</a>
									</div>
									<div className="Tickets mobile-only">
										<Button
											className={'Button Secondary'}
											href={
												game.ote_event?.cta_url
													? game.ote_event?.cta_url
													: game.ote_event?.vivenu_id
														? `${Urls.VivenuTicketsEventBaseUrl}/${game.ote_event.vivenu_id}`
														: null
											}
										>
											<FontAwesomeIcon icon={faTicket} /> Buy Tickets
										</Button>
									</div>
								</>
							) : null} */}
						</div>
					</div>
				) : null}
			</div>

			{/* TODO: This is more drill down than is ideal. Should create a context for the game so individual components can query that. */}
			<Tabs
				game={game}
				players={ote_games_ote_players_ote_teams ?? []}
				awayGameTeam={awayGameTeam}
				homeGameTeam={homeGameTeam}
			/>
			<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}></script>
		</>
	);
};

const Game = () => {
	const { id } = useParams();

	return (
		<Layout>
			<div className="Game">
				<PageHeader>Box Score</PageHeader>
				<LoadingBoundary>
					<ValidateUUID id={id} redirectPrefix="/games">
						<_Game />
					</ValidateUUID>
				</LoadingBoundary>
			</div>
		</Layout>
	);
};
export default Game;
